import { Navigate } from "react-router-dom";
import moment from "moment";
import { logout, refreshUserSession, updateUserAttribute } from "../authenticate";
import { useEffect, useState } from "react";
import SessionTimeoutModal from "../../components/modals/SessionTimeoutModal";
import { getUser, putUserNotificationSettings } from "../connector/userQueries";
import { getUserPool } from "../pools/pools";
import FreeTierModal from "../../components/modals/FreeTierModal";
import PendingApprovalModal from "../../components/modals/PendingApprovalModal";
import { getSubscription, getUserPaymentMethod } from "../connector/paypalQueries";
import { Box, CircularProgress } from "@mui/material";
import { clearAuth, getAuth, setAuth } from "../../contexts/authStore";
import { getInviteRequests } from "../connector/requestQueries";
import { Request } from "../../components/modals/ManagerApprovalModal/ManagerApprovalModal";
import { useToast } from "../../components/modals/ToastProvider/ToastProvider";

export const FREE_TIER_EXPIRY = 30; // Number of days before free tier expires

export function validateAuth(): boolean {
  const expiry = getAuth().expiry!;
  const refreshExpiry = getAuth().refreshExpiry!;
  try {
    if (expiry && moment(expiry) > moment()) {
      return true
    }
    else if (expiry && moment(expiry) <= moment()) {
      if (refreshExpiry && moment(refreshExpiry) <= moment()) {
        logout()
        return false
      } else {
        refreshUserSession()
        return true
      }
    }
    else {
      return false
    }
  } catch (error) {
    return false;
  }
}

export function idToken(): any {
  const idToken = getAuth().idToken!;
  return idToken
}

export default function Auth({ children }: { children?: React.ReactNode }) {
  const toast = useToast();
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [authenticated, setAuthenticated] = useState<boolean>(validateAuth());
  const [isFreeTierExpired, setIsFreeTierExpired] = useState<boolean>(true);
  const [isPendingApproval, setIsPendingApproval] = useState<boolean>(true);
  const [userData, setUserData] = useState<any>(null);

  useEffect(() => {
    if (authenticated) {
      setIsValidating(true);
      getUser(getUserPool()!.getCurrentUser()!.getUsername()).then((userData) => {
        setUserData(userData[0]);
        if (userData[0].accountType === "employee") {
          if (getAuth().requests && getAuth().requests !== "") {
            const scheduleId = getAuth().requests?.split("#")[1]
            getInviteRequests(scheduleId!).then((requests) => {
              if (requests) {
                const approvedJoinRequest = requests.filter((request: Request) => request.requesterId === userData[0]?.userId &&
                  request.status === "approved").length > 0;

                if (approvedJoinRequest) {
                  putUserNotificationSettings(userData[0].userId, false, false, false, false, false, false, true)
                  updateUserAttribute("custom:requests", "").then(() => {
                    updateUserAttribute("custom:user_type", getAuth().requests!);
                    setAuth({ user_type: getAuth().requests!, requests: "" } as any);
                    setTimeout(() => { }, 6000);
                    refreshUserSession();
                    setIsPendingApproval(false);
                    setIsValidating(false);
                  })
                } else {
                  clearAuth();
                  toast.showError("Your join request is still pending approval. Please wait for your manager to approve your request.");
                }
              } else {
                toast.showError("No join request found for this user. Please contact your manager.");
              }
            });
          } else {
            setIsPendingApproval(false);
            setIsValidating(false);
          }
          setIsFreeTierExpired(false);
        }
        else {
          setIsPendingApproval(false);
          if (moment(userData[0].created).isAfter(moment().subtract(FREE_TIER_EXPIRY, 'days'))) {
            setIsFreeTierExpired(false);
            setIsValidating(false);
          } else {
            getUserPaymentMethod(userData[0].userId).then((paymentMethod) => {
              paymentMethod = JSON.parse(paymentMethod.body)
              if (paymentMethod.paidThroughDate != null && moment(paymentMethod.paidThroughDate) >= moment()) {
                setIsFreeTierExpired(false);
              } else {
                if (paymentMethod.activeSubscriptionId != null) {
                  getSubscription(userData[0].userId, paymentMethod.activeSubscriptionId).then((subscription) => {
                    if (JSON.parse(subscription.body).status === "Active" || JSON.parse(subscription.body).status === "Pending") {
                      console.info(JSON.parse(subscription.body))
                      setIsFreeTierExpired(false);
                    }
                  })
                }
              }
              setIsValidating(false);
            });
          }
        }
      })
    }
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setAuthenticated(validateAuth());
    }, 60_000); // 1 minute

    return () => clearInterval(intervalId);
  }, []);

  const handleStillActive = () => {
    // Refresh session token or extend session
  };

  const handleTimeout = () => {
    // Clear session and redirect to login
    logout();
  };

  if (authenticated && isValidating) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.paper',
          zIndex: 9999
        }}
      >
        <CircularProgress size={120} color="info" />
      </Box>
    );
  }

  return authenticated ? (
    <>
      {children}
      {isPendingApproval && !isValidating ? (
        <PendingApprovalModal
          onClose={() => {
            setIsPendingApproval(false)
          }}
          open={isPendingApproval}
        />
      ) : isFreeTierExpired && !isValidating ? (
        <FreeTierModal
          onClose={() => {
            setIsFreeTierExpired(false)
          }}
          open={isFreeTierExpired}
          userData={userData}
        />
      ) : (
        <SessionTimeoutModal
          warningTime={10 * 60 * 1000}  // 10 minutes
          countdownTime={2 * 60 * 1000}  // 2 minutes
          onStillActive={handleStillActive}
          onTimeout={handleTimeout}
        />
      )}
    </>
  ) : (
    <Navigate to="/login" replace />
  );
}